/**
 * @file: types.ts
 *
 * Shared inbox types + the inbox context shape. The inbox is a composition
 * of two thread streams (feed activity + chats); this is the state the
 * inbox surface coordinates centrally.
 */

import {
  ChatConversationHandle,
  conversationHandleKey,
} from '@euler/app/flows/chat/lib/conversation/handle';
import { ChatContactPickerParams } from '@euler/app/flows/chat/lib/types';
import { FeedThreadInboxItem } from '@euler/model/feed';
import { BehaviorSubject } from 'rxjs';

export type InboxTab = 'notifications' | 'messages';

/** Which layout the inbox is presenting in. */
export type InboxLayoutMode = 'mobile' | 'desktop';

/**
 * A thread open in the inbox detail surface — either a feed activity thread or
 * a chat conversation. A conversation carries a resolved
 * {@link ChatConversationHandle}: the room is resolved at the open gate (with a
 * spinner) BEFORE it's selected, so the pane never renders a half-resolved room.
 */
export type InboxThreadItem =
  | { type: 'feed-thread'; thread: FeedThreadInboxItem }
  | {
      type: 'conversation';
      /**
       * The resolved conversation handle. Swapped bare→baked IN PLACE on the
       * first-send materialisation (a contact with no room yet) so the host
       * selection follows the now-real room (thread-list highlight + room info).
       */
      handle: ChatConversationHandle;
      /**
       * Stable identity of the open conversation, fixed when it's selected (the
       * peer for a contact-initiated chat, the room for a thread). Keys the
       * detail pane so a bare→baked materialisation updates it WITHOUT a remount.
       */
      selectionKey: string;
    };

/** Build the conversation selection for the inbox detail surface, stamping the
 * stable `selectionKey` from the handle at selection time. */
export function inboxConversation(
  handle: ChatConversationHandle,
): InboxThreadItem {
  return {
    type: 'conversation',
    handle,
    selectionKey: conversationHandleKey(handle),
  };
}

/** The active transient inbox panel (mutually exclusive — one open at a time). */
export type InboxOverlay =
  | { kind: 'search' }
  | { kind: 'settings' }
  | { kind: 'contacts' }
  | { kind: 'picker'; params: ChatContactPickerParams };

export interface InboxContextProps {
  /** Which layout the inbox is presenting in (mobile stack / desktop panes). */
  layoutMode$: BehaviorSubject<InboxLayoutMode>;
  /** The selected thread-list tab. */
  activeTab$: BehaviorSubject<InboxTab>;
  /** The thread open in the detail surface — a feed thread or a chat
   * conversation (see {@link InboxThreadItem}). */
  activeThread$: BehaviorSubject<InboxThreadItem | null>;
  /** The active transient overlay, if any. */
  overlay$: BehaviorSubject<InboxOverlay | null>;
  /** Whether the chat room-info surface is showing. */
  infoOpen$: BehaviorSubject<boolean>;
  /** Bumped to force the room-info surface to remount + refetch. */
  nonce$: BehaviorSubject<number>;
  /**
   * Bumped to ask the feed thread list to refresh silently — used by the
   * tab-bar "全部已读" action so the per-row badges catch up with the
   * just-issued mark-all-read without showing the pull indicator.
   */
  feedListReloadTick$: BehaviorSubject<number>;
}
